import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { get_office_app_data, mark_get_started_seen } from '../../api';
import { useApi } from '../../use_api';
import { useAuth } from '../../auth/AuthProvider';
import { compute_setup, type SetupItem } from './get-started';

// Office landing — guides the painter through the few setup fields that make the product work
// (booking email, service areas, specialties, availability, then strengths/jobs/etc.). Pure
// completion-driven: shows only what's left and reads "all set" once the essentials are done.
// First run (the app-data marker is absent) shows a one-time welcome intro + stamps the marker.
// See docs/get-started.md.

const PROFILE_ROUTE = '/a/captomi_office/profile';

export function GetStartedView() {
  const { state } = useAuth();
  const signed_in = state.status === 'signed_in';
  const can_write = signed_in && state.session.role !== 'member';
  const first_name = signed_in ? state.session.first_name : '';

  const { loading, data, error } = useApi(() => get_office_app_data(), []);
  const app = (data?.data ?? {}) as Record<string, unknown>;
  const setup = compute_setup(app);
  const first_run = !setup.wizard_seen;

  // Stamp the one-time "wizard seen" marker server-side on first run (owners/admins only).
  const stamped = useRef(false);
  useEffect(() => {
    if (!loading && data && can_write && first_run && !stamped.current) {
      stamped.current = true;
      void mark_get_started_seen().catch(() => {});
    }
  }, [loading, data, can_write, first_run]);

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">Couldn’t load your setup.</p>;

  const must = setup.items.filter((i) => i.tier === 'must');
  const should = setup.items.filter((i) => i.tier === 'should');
  const should_incomplete = should.some((i) => !i.complete);

  return (
    <div style={{ maxWidth: 680, display: 'grid', gap: 16 }}>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{setup.all_must_complete ? 'You’re all set' : 'Get started'}</h2>

        {first_run && !setup.all_must_complete && (
          <p className="muted" style={{ marginTop: 0 }}>
            Welcome{first_name ? `, ${first_name}` : ''} — let’s finish setting up your AI receptionist.
            A few details make sure your leads reach you and your receptionist sounds like your business.
          </p>
        )}

        <p className="muted" style={{ margin: '4px 0 14px', fontWeight: 600 }}>
          {setup.must_done} of {setup.must_total} essentials done
        </p>

        {setup.all_must_complete ? (
          <p style={{ margin: 0 }}>
            Your essentials are complete.{' '}
            {should_incomplete
              ? 'A few optional details below can make your receptionist even better.'
              : 'Nice work — your profile is in great shape.'}
          </p>
        ) : (
          <Checklist items={must} can_write={can_write} />
        )}
      </div>

      {should_incomplete && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Strengthen your profile</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Optional, but they help your receptionist sell your business on the call.
          </p>
          <Checklist items={should} can_write={can_write} />
        </div>
      )}
    </div>
  );
}

function Checklist({ items, can_write }: { items: SetupItem[]; can_write: boolean }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {items.map((i) => (
        <div key={i.key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span
            aria-hidden
            style={{ fontSize: 16, lineHeight: '20px', color: i.complete ? 'var(--success)' : 'var(--text-3)' }}
          >
            {i.complete ? '✓' : '○'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{i.label}</div>
            {!i.complete && <div className="muted" style={{ fontSize: 13 }}>{i.why}</div>}
          </div>
          {!i.complete && can_write && (
            <Link
              className="btn secondary"
              to={`${PROFILE_ROUTE}?focus=${encodeURIComponent(i.field_label)}`}
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              Set up
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}
